// src/services/authService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import apiConfig, { cancelAllRequests, clearAuthHeader, LOGOUT_FLAGS } from 'configs/fetchInterceptor';
import axiosService from 'services/axiosService';
import FirebaseService from 'services/FirebaseService';
import app from 'configs/ConfigIndex';
import { getLocale } from 'utils/helpers';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';
import { getStorageAuthItem, removeStorageAuthItem, setStorageAuthItem } from 'stores/auth';
import { API } from 'constants/Api';
import { getClientIp } from 'utils/getClientIp';
import { notifyLogin, notifyLogout, setCurrentUserId } from 'utils/multiTabSync';
import { clearAllExecutionTrackers } from 'hooks/useApi';

// *******************************************************************************
// INTERNAL STATE
// *******************************************************************************
let signInByTokenInProgress = false;

// *******************************************************************************
// METHODS
// *******************************************************************************
const getUSer = () => useZustand('auth', 'user', false);

const signUp = async body => {
  const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
  setLoadingAppStore({ visible: true, title: 'creating_user_wait', translucid: true });

  try {
    // ───────────────────────────────────────────────────────────────────────────
    // STEP 0: Extract loginType and login from body
    // ───────────────────────────────────────────────────────────────────────────
    const loginType = body?.loginType || 'email'; // Default: 'email' | Future: 'phone', 'cpf', etc.
    const login = body?.login || body?.email; // Fallback to email for backward compatibility
    const password = body?.password;

    // Validate loginType
    if (loginType !== 'email') {
      appService.notification({
        key: 'signupError',
        flag: 'warning',
        title: 'authentication_fail',
        description: `Login type "${loginType}" not supported yet. Only "email" is supported.`,
        duration: 6,
      });

      setLoadingAppStore(false);
      return { success: false, error: 'unsupported_login_type' };
    }

    // For email loginType, use login as email
    const email = loginType === 'email' ? login : null;

    if (!email) {
      appService.notification({
        key: 'signupError',
        flag: 'warning',
        title: 'authentication_fail',
        description: 'Email is required for registration',
        duration: 6,
      });

      setLoadingAppStore(false);
      return { success: false, error: 'email_required' };
    }

    let firebaseUser = null;
    let createdNewUser = false;

    // STEP 1: Firebase Authentication (email only for now)
    try {
      const firebaseDoc = await FirebaseService.signUpEmailRequest(email, password);
      firebaseUser = firebaseDoc?.user;
      createdNewUser = true;
    } catch (signupErr) {
      if (signupErr?.message === 'auth/email-already-in-use') {
        try {
          const signInResult = await FirebaseService.signInEmailRequest({
            email: email,
            password: password,
          });

          // FirebaseService.signInEmailRequest returns the error instead of throwing
          // Check if signInResult is an error (has code property or no user property)
          if (signInResult?.code || signInResult instanceof Error || !signInResult?.user) {
            appService.notification({
              key: 'signupError',
              flag: 'warning',
              title: 'authentication_fail',
              description: 'invalid_email_or_password',
              duration: 6,
            });

            setLoadingAppStore(false);
            return { success: false, error: 'invalid_credentials' };
          }

          firebaseUser = signInResult?.user;
          createdNewUser = false;
        } catch (signInErr) {
          appService.notification({
            key: 'signupError',
            flag: 'warning',
            title: 'authentication_fail',
            description: 'invalid_email_or_password',
            duration: 6,
          });

          setLoadingAppStore(false);
          return { success: false, error: 'invalid_credentials' };
        }
      } else {
        appService.notification({
          key: 'signupError',
          flag: 'warning',
          title: 'authentication_fail',
          description: signupErr?.message || 'firebase_signup_failed',
          duration: 6,
        });

        setLoadingAppStore(false);
        return { success: false, error: signupErr?.message };
      }
    }

    if (!firebaseUser?.uid) {
      appService.notification({
        key: 'signupError',
        flag: 'warning',
        title: 'account_creation_error',
        description: 'account_creation_error_description',
        duration: 6,
      });

      //

      setLoadingAppStore(false);
      return { success: false, error: 'firebase_user_not_created' };
    }

    const firebaseIdToken = await firebaseUser.getIdToken();

    if (!firebaseIdToken) {
      appService.notification({
        key: 'signupError',
        flag: 'warning',
        title: 'authentication_fail',
        description: 'failed_to_obtain_firebase_token',
        duration: 6,
      });

      setLoadingAppStore(false);
      return { success: false, error: 'firebase_token_failed' };
    }

    // STEP 2: Prepare payload for backend (NEW FORMAT)
    const signupPayload = {
      ...body,
      // NEW: loginType and login (replaces email field)
      loginType, // 'email' | 'phone' | 'cpf' (future)
      login, // The actual login value (email@domain.com, phone number, cpf, etc.)

      // REMOVED: email field (replaced by login)
      // KEPT: All other fields remain unchanged
      emailConfirmation: !!body?.emailCode,
      mobileConfirmation: body?.validateMobile && !!body?.mobileCode,
      firebaseUid: firebaseUser.uid,
      firebaseIsNewUser: createdNewUser,
      firebaseToken: firebaseIdToken,
      provider: 'firebase',
      service: 'email', // Keep 'email' for now since we only support email auth
    };

    // Remove email field from payload (replaced by login)
    delete signupPayload.email;

    // STEP 3: Register account on backend
    await axiosService.post({
      url: API.AUTH.SIGNUP,
      data: signupPayload,
      returnAsObject: true,
      headers: { 'x-forcerequest': true },
    });

    // STEP 4: Sign in with new credentials
    const credentials = {
      loginType,
      login,
      password,
    };

    // Set firstUse flag for new users
    setStorageAuthItem('firstUse', true);

    await signIn(credentials);
    setLoadingAppStore(false);
  } catch (err) {
    // Don't log API Tracker aborts (already logged by tracker)
    if (!err?.isApiTrackerAbort) {
      console.error('[signUp]', err);
    }

    appService.notification({
      key: 'signupError',
      flag: 'warning',
      title: 'authentication_fail',
      description: err?.code || err?.message || 'unexpected_error',
      duration: 6,
    });

    setLoadingAppStore(false);

    return { success: false, error: err?.message };
  }
};

const signIn = async credentials => {
  const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
  setLoadingAppStore({ visible: true, title: null, translucid: true });

  try {
    // ───────────────────────────────────────────────────────────────────────────
    // STEP 0: Extract loginType and login from credentials
    // ───────────────────────────────────────────────────────────────────────────
    const loginType = credentials?.loginType || 'email'; // Default: 'email' | Future: 'phone', 'cpf', etc.
    const login = credentials?.login || credentials?.email; // Fallback to email for backward compatibility
    const password = credentials?.password;

    // Validate loginType
    if (loginType !== 'email') {
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: `Login type "${loginType}" not supported yet. Only "email" is supported.`,
          duration: 6,
        });
      }, 100);

      return { success: false, error: 'unsupported_login_type' };
    }

    // For email loginType, use login as email
    const email = loginType === 'email' ? login : null;

    if (!email) {
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: 'Email is required for sign in',
          duration: 6,
        });
      }, 100);

      return { success: false, error: 'email_required' };
    }

    // STEP 1: Firebase Authentication (email only for now)
    let firebaseUser = null;

    try {
      const firebaseDoc = await FirebaseService.signInEmailRequest({
        email: email,
        password: password,
      });
      firebaseUser = firebaseDoc?.user;
    } catch (signInErr) {
      console.error('[signIn] Firebase sign in failed:', signInErr?.message);

      // Hide loading before showing notification
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      // Show notification after a small delay to ensure loading is hidden
      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: 'invalid_email_or_password',
          duration: 6,
        });
      }, 100);

      return { success: false, error: 'invalid_credentials' };
    }

    if (!firebaseUser?.uid) {
      // Hide loading before showing notification
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: 'user_or_password_invalid',
          duration: 6,
        });
      }, 100);

      return { success: false, error: 'firebase_user_not_found' };
    }

    const firebaseIdToken = await firebaseUser.getIdToken();

    if (!firebaseIdToken) {
      // Hide loading before showing notification
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: 'failed_to_obtain_firebase_token',
          duration: 6,
        });
      }, 100);

      return { success: false, error: 'firebase_token_failed' };
    }

    // STEP 2: Authenticate with backend using Firebase token (NEW FORMAT)
    const signInPayload = {
      // NEW: loginType and login (replaces email field)
      loginType, // 'email' | 'phone' | 'cpf' (future)
      login, // The actual login value (email@domain.com, phone number, cpf, etc.)

      // Firebase authentication data
      firebaseUid: firebaseUser.uid,
      firebaseToken: firebaseIdToken,
      provider: 'firebase',
      service: 'email', // Keep 'email' for now since we only support email auth
    };

    const [data, result] = await axiosService.post({
      url: API.AUTH.SIGNIN,
      data: signInPayload,
      returnAsObject: true,
      headers: { 'x-forcerequest': true },
    });

    // Token now comes from summary.tokenInfo.token
    const token = result?.summary?.tokenInfo?.token;

    if (token) {
      const setTokenStore = useZustand('auth', 'setToken', false);
      setTokenStore(token);

      // appService.notification({
      //   key: 'signInSuccess',
      //   flag: 'success',
      //   title: 'welcome',
      //   description: 'login_success',
      //   duration: 2,
      // });

      // Pass the complete result to avoid a second request
      await signInByToken(token, result);
      // return {
      //   success: true,
      //   data: data,
      //   token: token,
      // };
    } else {
      // Hide loading before showing notification
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: result?.error?.message || 'authentication_failed',
          duration: 6,
        });
      }, 100);

      return { success: false, error: result?.error };
    }
  } catch (err) {
    console.error('[signIn] Backend error:', err);

    // Hide loading before showing notification
    setLoadingAppStore({ visible: false, extendedDelayToHide: 0, translucid: true });

    // Check if it's an authentication-related error (account not found, invalid credentials, etc.)
    const errorMessage = err?.response?.data?.message || err?.message || '';
    const isAuthError =
      errorMessage.toLowerCase().includes('account not found') ||
      errorMessage.toLowerCase().includes('invalid') ||
      errorMessage.toLowerCase().includes('not found') ||
      err?.response?.status === 400 ||
      err?.response?.status === 401 ||
      err?.response?.status === 403;

    // Only show notification for auth errors, let fetchInterceptor handle real server errors
    if (isAuthError) {
      setTimeout(() => {
        appService.notification({
          key: 'signInError',
          flag: 'warning',
          title: 'authentication_fail',
          description: 'invalid_email_or_password',
          duration: 6,
        });
      }, 100);
    }

    return { success: false, error: err?.message };
  }
};

const signInRefresh = async () => {
  const token = getStorageAuthItem('token');
  if (!token) return null;

  await signInByToken(token);
};

const signInByToken = async (token, apiResult = null) => {
  if (!token) {
    await signOut({ replace: true });
    return null;
  }

  // Prevent duplicate simultaneous executions
  if (signInByTokenInProgress) {
    return null;
  }

  // Mark as in progress BEFORE any async operations
  signInByTokenInProgress = true;

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 0: Save token to Zustand BEFORE making request
  // This ensures backend receives Authorization header in next requests
  // ─────────────────────────────────────────────────────────────────────────
  const setTokenStore = useZustand('auth', 'setToken', false);
  setTokenStore(token);

  try {
    let payload;

    // STEP 1: Get account data
    // If apiResult is provided (from signIn), use it directly to avoid a second request
    // Otherwise, fetch from API (when user refreshes page)
    if (apiResult) {
      payload = apiResult?.data ?? null;
    } else {
      // Fetch account data using token (page refresh scenario)
      const [data, result] = await axiosService.get({
        url: API.AUTH.SIGNIN_BY_TOKEN,
        headers: { 'x-subscriptionid': null },
        returnAsObject: true,
      });
      payload = data ?? null;
    }

    // STEP 2: Extract account and user data
    const account =
      payload?.account ?? payload?.Account ?? (payload && payload._id ? payload : null);
    const user =
      payload?.user ?? payload?.User ?? account?.user ?? (account && account._id ? account : null);

    if (!user) {
      console.error('[signInByToken] User not found in response');
      // Use isForced: true since this is not a user-initiated logout
      await signOut({ replace: true, isForced: true });
      return null;
    }

    // STEP 3: Get Zustand setters
    const setUser = useZustand('auth', 'setUser', false);
    // const setSubscription = useZustand('auth', 'setSubscription', false);
    // const setCompany = useZustand('auth', 'setCompany', false);
    const setNavCollapsedStore = useZustand('theme', 'setNavCollapsed', false);

    // STEP 4: Process user data
    // if (user?.birthdate) user.birthdate = dayjs(user.birthdate);

    // STEP 5: Save account and subscription to auth service
    // authService.account = account || user || null;
    // authService.subscription = payload?.subscription ?? payload?.Subscription ?? null;

    // STEP 6: Handle subscription selection
    // const subscriptionKey = `userId[${user?._id}].subscriptionId`;
    // const savedSubId = getStorageAuthItem(subscriptionKey);
    // const availableSubs = user?.subscriptions?.filter(x => x?.status === 2) || [];
    // const selectedSubscription =
    //   availableSubs?.find(x => x?._id === savedSubId) || availableSubs?.[0] || null;

    // STEP 7: Handle company selection
    // const companyKey = `userId[${user?._id}].companyId`;
    // const savedCompanyId = getStorageAuthItem(companyKey);
    // const availableCompanies = selectedSubscription?.__companies || [];
    // const selectedCompany =
    //   availableCompanies?.find(x => x?._id === savedCompanyId) || availableCompanies?.[0] || null;

    // STEP 8: Update Zustand store (which also saves to localStorage)
    setUser(user);

    // STEP 8.1: Notify other tabs about login
    setCurrentUserId(user?._id);
    notifyLogin(user?._id);

    if (!user?.subscriptions?.length) setNavCollapsedStore(true);

    // await setSubscription(selectedSubscription);
    // setCompany(selectedCompany);

    // STEP 9: Initialize SSE connection for parameters (only once after user is loaded)
    const appState = useZustand('app', 'state', false);

    // Start the SSE connection only if not already connected
    if (typeof appState?.initSSE === 'function' && !appState?.sseConnected) {
      appState.initSSE();
    }

    // STEP 10: Check for redirectAfterLogin in localStorage
    const redirectAfterLogin = getStorageAuthItem('redirectAfterLogin');
    if (redirectAfterLogin) {
      removeStorageAuthItem('redirectAfterLogin');
      // Use setTimeout to ensure state updates are complete before navigation
      setTimeout(() => {
        navigateTo(redirectAfterLogin, { replace: true });
      }, 100);
    }

    return {
      success: true,
      user,
      // subscription: selectedSubscription,
      // company: selectedCompany,
    };
  } catch (err) {
    console.error('[signInByToken]', err);
    // Use isForced: true to indicate this is NOT a user-initiated logout
    // This ensures the "session expired" notification will be shown
    await signOut({ replace: true, isForced: true });
    return null;
  } finally {
    // Reset flag to allow future logins
    signInByTokenInProgress = false;
  }
};

const signOut = async (opts = {}) => {
  const { replace = true, skipNavigation = false, isForced = false } = opts;

  const setReadyStore = useZustand('module', 'setReady', false);
  const setTokenStore = useZustand('auth', 'setToken', false);
  const setUser = useZustand('auth', 'setUser', false);
  const setSubscription = useZustand('auth', 'setSubscription', false);
  const setCompany = useZustand('auth', 'setCompany', false);
  const setParametersStore = useZustand('app', 'setParameters', false);
  const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
  const setQuote = useZustand('theme', 'setQuote', false);

  setReadyStore(false);
  setLoadingAppStore({
    visible: true,
    title: 'exiting_application',
    translucid: false,
    extendedDelayToHide: 400,
  });

  // Notify other tabs about logout BEFORE clearing state (only if explicit logout)
  if (!isForced) {
    notifyLogout();
    // Mark this tab as doing explicit logout (not forced/expired)
    // This prevents showing "session expired" message in THIS tab
    sessionStorage.setItem(LOGOUT_FLAGS.EXPLICIT_LOGOUT, 'true');
  }

  setTimeout(() => $doExit(), 400);
  async function $doExit() {
    try {
      // Force-close all open modals (keyed and imperative createRoot) before clearing state
      appService.destroyAllModals();

      // Close SSE connection before logout
      const appState = useZustand('app', 'state', false);
      if (typeof appState?.closeSSE === 'function') {
        appState.closeSSE();
      }

      // Reset signInByToken flag to allow future logins
      signInByTokenInProgress = false;

      // Clear all useApi execution trackers to ensure clean state on next login
      clearAllExecutionTrackers();

      clearAuthHeader();
      cancelAllRequests();

      try {
        await FirebaseService.signOutRequest?.();
      } catch {}

      removeStorageAuthItem('token');
      removeStorageAuthItem('firstUse');

      if (typeof sessionStorage !== 'undefined') {
        // Preserve all logout flags before clearing
        const explicitLogout = sessionStorage.getItem(LOGOUT_FLAGS.EXPLICIT_LOGOUT);
        const sessionExpiredAuto = sessionStorage.getItem(LOGOUT_FLAGS.SESSION_EXPIRED_AUTO);
        const sessionExpiredRequest = sessionStorage.getItem(LOGOUT_FLAGS.SESSION_EXPIRED_REQUEST);
        const sessionExpiredOtherTab = sessionStorage.getItem(LOGOUT_FLAGS.SESSION_EXPIRED_OTHER_TAB);

        sessionStorage.clear();

        // Restore flags so SignIn can use them to show appropriate notification
        if (explicitLogout) {
          sessionStorage.setItem(LOGOUT_FLAGS.EXPLICIT_LOGOUT, explicitLogout);
        }
        if (sessionExpiredAuto) {
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_AUTO, sessionExpiredAuto);
        }
        if (sessionExpiredRequest) {
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_REQUEST, sessionExpiredRequest);
        }
        if (sessionExpiredOtherTab) {
          sessionStorage.setItem(LOGOUT_FLAGS.SESSION_EXPIRED_OTHER_TAB, sessionExpiredOtherTab);
        }
      }

      // Clear DOM state
      try {
        document.body.classList.remove('fix-scrollbar');
        document.body.classList.add('non-fix-scrollbar');
      } catch {}

      // Clear all Zustand store auth state (using getState, not hook)
      setTokenStore(null);
      setUser(null);
      setSubscription(null);
      setCompany(null);
      setParametersStore(null);

      if (!skipNavigation) {
        const loginHref = `${app.AUTH_PREFIX_PATH}/login`;
        navigateTo(loginHref, { replace });
      }
    } finally {
    }
  }
};

const sendEmailRegistrationCode = async (
  { name, email, emailData = {}, loginType = 'email' },
  rule = 'just_send_email'
) => {
  const payload = {
    ...emailData,
    name,
    email,
    ip: await getClientIp()?.ip,
    language: getLocale(true).toLowerCase(),
    loginType, // 'email' | 'phone' | 'cpf' (future)
    rule, // 'email_must_exists' | 'email_must_not_exists' || 'just_send_email'
  };

  const [data, result] = await axiosService.post({
    url: API.AUTH.SEND_EMAIL_REGISTRATION_CODE,
    headers: { 'x-forcerequest': true },
    data: payload,
    returnAsObject: true,
  });

  return typeof result?.success === 'undefined' ? 'error' : !!result?.success;
};

const validateEmailCode = async values => {
  const [data, result] = await axiosService.post({
    url: API.AUTH.VALIDATE_EMAIL_CODE,
    headers: { 'x-forcerequest': true },
    data: values,
    returnAsObject: true,
  });

  return result?.success;
};

const sendSmsRegistrationCode = async ({
  name,
  mobile,
  mobileCountryCode,
  loginType = 'email',
}) => {
  const payload = {
    name,
    mobile,
    mobileCountryCode,
    ip: await getClientIp()?.ip,
    language: getLocale(true).toLowerCase(),
    loginType, // 'email' | 'phone' | 'cpf' (future)
  };

  const [data, result] = await axiosService.post({
    url: API.AUTH.SEND_SMS_REGISTRATION_CODE,
    headers: { 'x-forcerequest': true },
    data: payload,
    returnAsObject: true,
  });

  return data;

  // return typeof result?.success === 'undefined' ? 'error' : !!result?.success;
};

const validateSmsCode = async values => {
  const [data, result] = await axiosService.post({
    url: API.AUTH.VALIDATE_SMS_CODE,
    headers: { 'x-forcerequest': true },
    data: values,
    returnAsObject: true,
  });

  return result?.success;
};

const sendEmailResetCode = async ({ email, loginType = 'email' }) => {
  const payload = {
    email,
    ip: await getClientIp()?.ip,
    language: getLocale(true).toLowerCase(),
    loginType, // 'email' | 'phone' | 'cpf' (future)
  };

  const [data, result] = await axiosService.post({
    url: API.AUTH.SEND_EMAIL_RESET_CODE,
    headers: { 'x-forcerequest': true },
    data: payload,
    returnAsObject: true,
  });

  return typeof result?.success === 'undefined' ? 'error' : !!result?.success;
};

const validateResetCode = async values => {
  const [data, result] = await axiosService.post({
    url: API.AUTH.VALIDATE_RESET_CODE,
    headers: { 'x-forcerequest': true },
    data: values,
    returnAsObject: true,
  });

  return result?.success;
};

const resetPassword = async body => {
  try {
    const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);

    setLoadingAppStore({ visible: true, title: 'resetting_password', translucid: true });

    // STEP 1: Reset password on Firebase
    // Note: We're using a custom code flow instead of Firebase's native reset
    // to maintain consistency with the registration flow

    // STEP 2: Update password on backend
    const resetPayload = {
      email: body?.email,
      password: body?.password?.toString(),
    };

    const [account, result] = await axiosService.post({
      url: API.AUTH.RESET_PASSWORD,
      data: resetPayload,
      returnAsObject: true,
      headers: { 'x-forcerequest': true },
    });

    if (result?.success) {
      appService.notification({
        key: 'resetSuccess',
        flag: 'success',
        title: 'password_reset_success',
        description: translateX('password_reset_success_description', {
          email: <strong>{body?.email}</strong>,
        }),
        duration: 10,
      });

      return { success: true };
    } else {
      appService.notification({
        key: 'resetError',
        flag: 'warning',
        title: 'password_reset_fail',
        description: result?.error?.message || 'password_reset_failed',
        duration: 6,
      });

      setLoadingAppStore(false);
      return { success: false, error: result?.error };
    }
  } catch (err) {
    console.error('[resetPassword]', err);

    appService.notification({
      key: 'resetError',
      flag: 'warning',
      title: 'password_reset_fail',
      description: err?.message || 'unexpected_error',
      duration: 6,
    });

    const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
    setLoadingAppStore(false);
    return { success: false, error: err?.message };
  }
};

// *******************************************************************************
// SERVICE
// *******************************************************************************
const authService = {
  // miscellaneous
  account: null,
  subscription: null,
  getUSer,

  // access
  signUp,
  signIn,
  signInByToken,
  signOut,
  signInRefresh,

  // messaging,
  sendEmailRegistrationCode,
  validateEmailCode,

  sendSmsRegistrationCode,
  validateSmsCode,

  sendEmailResetCode,
  validateResetCode,
  resetPassword,
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default authService;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function navigateTo(href, { replace = true } = {}) {
  try {
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({}, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch {
    replace ? window.location.replace(href) : (window.location.href = href);
  }
}
